import { isHoliday } from "nyse-holidays";

function mean(xs) {
  return xs.reduce((a, b) => a + b, 0) / xs.length;
}

function std(xs, ddof = 1) {
  const n = xs.length;
  if (n - ddof <= 0) return NaN;
  const m = mean(xs);
  const v = xs.reduce((acc, x) => acc + (x - m) ** 2, 0) / (n - ddof);
  return Math.sqrt(v);
}

function parseIsoToLocalDate(date) {
  // date: "YYYY-MM-DD" -> local Date at midnight (matches how nyse-holidays expects dates)
  const [y, m, d] = date.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function isTradingDayNY(dateStr) {
  const dt = parseIsoToLocalDate(dateStr);
  const dow = dt.getDay(); // 0 Sun ... 6 Sat
  if (dow === 0 || dow === 6) return false;
  return !isHoliday(dt);
}

function parseIsoDayUTC(date) {
  // Use UTC midnight for calendar-diff math
  return new Date(`${date}T00:00:00Z`);
}

function dayDiffCalendar(start, end) {
  const a = parseIsoDayUTC(start).getTime();
  const b = parseIsoDayUTC(end).getTime();
  return Math.round((b - a) / (24 * 60 * 60 * 1000));
}

function getReturnsInRangeWithDates(
  dailyReturns,
  startDate,
  endDate,
  ignoreNonTradingDays
) {
  if (startDate > endDate) return [];

  const dates = Array.from(dailyReturns.keys())
    .filter((d) => d >= startDate && d <= endDate)
    .sort((a, b) => a.localeCompare(b));

  const out = [];

  for (const d of dates) {
    if (ignoreNonTradingDays && !isTradingDayNY(d)) continue;

    const r = dailyReturns.get(d);
    if (r === undefined) continue;
    if (!Number.isFinite(r)) continue;

    out.push({ date: d, r });
  }

  return out;
}

/**
 * Sharpe ratio (annualized) from DAILY returns in [startDate, endDate].
 * Sharpe = sqrt(252) * mean(excess_daily) / std(excess_daily)
 */
function sharpeRatio(dailyReturns, startDate, endDate, opts = {}) {
  const {
    tradingDays = 252,
    riskFreeRateAnnual = 0,
    ddof = 1,
    ignoreNonTradingDays = true,
  } = opts;

  const series = getReturnsInRangeWithDates(
    dailyReturns,
    startDate,
    endDate,
    ignoreNonTradingDays
  );
  const rets = series.map((x) => x.r);
  if (rets.length < (ddof === 1 ? 2 : 1)) return NaN;

  const rfDaily = riskFreeRateAnnual / tradingDays;
  const excess = rets.map((r) => r - rfDaily);

  const mu = mean(excess);
  const sigma = std(excess, ddof);
  if (!Number.isFinite(sigma) || sigma === 0) return NaN;

  return (mu / sigma) * Math.sqrt(tradingDays);
}

/**
 * Sortino ratio (annualized) from DAILY returns in [startDate, endDate].
 * Sortino = sqrt(252) * mean(excess_daily) / downsideDeviation
 *
 * downsideDeviation is std of negative (excess - threshold).
 */
function sortinoRatio(dailyReturns, startDate, endDate, opts = {}) {
  const {
    tradingDays = 252,
    riskFreeRateAnnual = 0,
    ddof = 1,
    sortinoThreshold = 0,
    ignoreNonTradingDays = true,
  } = opts;

  const series = getReturnsInRangeWithDates(
    dailyReturns,
    startDate,
    endDate,
    ignoreNonTradingDays
  );
  const rets = series.map((x) => x.r);
  if (rets.length === 0) return NaN;

  const rfDaily = riskFreeRateAnnual / tradingDays;
  const excess = rets.map((r) => r - rfDaily);

  const downside = excess
    .map((x) => x - sortinoThreshold)
    .filter((x) => x < 0);

  if (downside.length < (ddof === 1 ? 2 : 1)) return NaN;

  const mu = mean(excess);
  const downsideSigma = std(downside, ddof);
  if (!Number.isFinite(downsideSigma) || downsideSigma === 0) return NaN;

  return (mu / downsideSigma) * Math.sqrt(tradingDays);
}

/**
 * Max drawdown over [startDate, endDate] from DAILY returns.
 * Returns NEGATIVE percentage (e.g. -18.3).
 */
function maxDrawdownPct(dailyReturns, startDate, endDate, opts = {}) {
  const { ignoreNonTradingDays = true } = opts;

  const series = getReturnsInRangeWithDates(
    dailyReturns,
    startDate,
    endDate,
    ignoreNonTradingDays
  );
  if (series.length === 0) return NaN;

  let equity = 1.0;
  let peak = 1.0;
  let maxDD = 0.0;

  for (const { r } of series) {
    equity *= 1 + r;
    if (equity > peak) peak = equity;

    const dd = peak === 0 ? NaN : equity / peak - 1;
    if (Number.isFinite(dd) && dd < maxDD) maxDD = dd;
  }

  return maxDD * 100;
}

/**
 * Calculate period return from NAV series using (endNav / startNav) - 1
 */
function periodReturnFromNav(navSeries, startDate, endDate) {
  const entries = navSeries instanceof Map 
    ? Array.from(navSeries.entries())
    : Object.entries(navSeries);
  
  if (entries.length === 0) return NaN;

  const sortedDates = entries.map(([date]) => date).sort((a, b) => a.localeCompare(b));

  const findNearest = (targetDate) => {
    let nearestDate = null;
    for (const date of sortedDates) {
      if (date <= targetDate) {
        nearestDate = date;
      } else {
        break;
      }
    }
    return nearestDate ?? sortedDates[0];
  };

  const effectiveStartDate = findNearest(startDate);
  const effectiveEndDate = findNearest(endDate);

  if (!effectiveStartDate || !effectiveEndDate) return NaN;

  const startNav = navSeries instanceof Map 
    ? navSeries.get(effectiveStartDate)
    : navSeries[effectiveStartDate];
  const endNav = navSeries instanceof Map 
    ? navSeries.get(effectiveEndDate)
    : navSeries[effectiveEndDate];

  if (startNav === undefined || endNav === undefined) return NaN;
  if (startNav === 0) return NaN;

  return (endNav / startNav) - 1;
}

function navToDailyReturns(navByDate, opts = {}) {
  const { ignoreNonTradingDays = true } = opts;

  const dates = Array.from(navByDate.keys()).sort((a, b) =>
    a.localeCompare(b)
  );
  const out = new Map();

  let prevNav = null;
  let prevDate = null;

  for (const d of dates) {
    if (ignoreNonTradingDays && !isTradingDayNY(d)) continue;

    const nav = navByDate.get(d);
    if (nav === undefined || !Number.isFinite(nav) || nav <= 0) continue;

    // First valid day has no prior day return; skip it (same as most libs)
    if (prevNav !== null && prevDate !== null) {
      const r = nav / prevNav - 1;
      if (Number.isFinite(r)) out.set(d, r);
    }

    prevNav = nav;
    prevDate = d;
  }

  return out;
}

function backtestSummaryFromNav(navSeries, startDate, endDate, opts = {}) {
  const dailyReturns = navToDailyReturns(navSeries, {
    ignoreNonTradingDays: opts.ignoreNonTradingDays ?? true,
  });

  // Get the first NAV date directly from the series (sorted, within date range)
  const navDates = Array.from(navSeries.keys())
    .filter((d) => d >= startDate && d <= endDate)
    .sort((a, b) => a.localeCompare(b));
  const firstNavDate = navDates.length > 0 ? navDates[0] : startDate;

  // Pass the first NAV date and navSeries to use for TWR calculation
  return backtestSummary(dailyReturns, startDate, endDate, {
    ...opts,
    firstNavDate, // Pass the first NAV date from NAV series
    navSeries, // Pass the original NAV series for TWR calculation
  });
}

/**
 * Full backtest summary matching the fields in your sample output.
 * Percent fields are returned as percent numbers (e.g. -0.76 means -0.76%).
 *
 * By default, filters out weekends/NYSE holidays for Sharpe/Sortino/Vol/AvgDD/Drawdowns.
 * TotalTWR/Annualized also uses the filtered series (which is standard).
 */
function backtestSummary(dailyReturns, startDate, endDate, opts = {}) {
  const {
    tradingDays = 252,
    riskFreeRateAnnual = 0,
    ddof = 1,
    sortinoThreshold = 0,
    ignoreNonTradingDays = true,
    firstNavDate, // First NAV date from the original series
    navSeries, // Original NAV series for TWR calculation
  } = opts;

  const series = getReturnsInRangeWithDates(
    dailyReturns,
    startDate,
    endDate,
    ignoreNonTradingDays
  );
  const n = series.length;

  // Use the first NAV date as effectiveStartDate if provided, otherwise use first return date
  // This ensures we capture the full NAV period, not just when returns start being calculated
  const firstReturnDate = n ? series[0].date : null;
  const effectiveStartDate = firstNavDate 
    ? firstNavDate 
    : (firstReturnDate ?? startDate);
  const effectiveEndDate = n ? series[n - 1].date : null;

  // Equity curve + drawdowns + worst drawdown details (peak -> trough -> recovery)
  let equity = 1.0;

  let currentPeakValue = 1.0;
  let currentPeakDate = effectiveStartDate ?? startDate;

  let worstDD = 0.0;
  let worstPeakDate = currentPeakDate;
  let worstPeakValue = 1.0;
  let worstTroughDate = currentPeakDate;
  let worstTroughValue = 1.0;

  const drawdowns = [];
  const equityPath = [];

  for (const { date, r } of series) {
    equity *= 1 + r;

    if (equity >= currentPeakValue) {
      currentPeakValue = equity;
      currentPeakDate = date;
    }

    const dd =
      currentPeakValue === 0 ? NaN : equity / currentPeakValue - 1;
    if (Number.isFinite(dd)) drawdowns.push(dd);

    if (dd < worstDD) {
      worstDD = dd;
      worstPeakDate = currentPeakDate;
      worstPeakValue = currentPeakValue;
      worstTroughDate = date;
      worstTroughValue = equity;
    }

    equityPath.push({ date, equity });
  }

  // Calculate TWR using periodReturnFromNav if navSeries is provided, otherwise use equity curve
  // Use the original selected dates (startDate, endDate) from periods, not effective dates
  let totalTwr;
  if (navSeries && startDate && endDate) {
    const twr = periodReturnFromNav(navSeries, startDate, endDate);
    totalTwr = !isNaN(twr) ? twr : (n > 0 ? equity - 1 : NaN);
  } else {
    totalTwr = n > 0 ? equity - 1 : NaN;
  }
  // Calculate annualized return based on actual date range
  let annualized = NaN;
  if (effectiveStartDate && effectiveEndDate && Number.isFinite(totalTwr)) {
    const start = new Date(effectiveStartDate);
    const end = new Date(effectiveEndDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const years = diffDays / 365.25;
    
    if (years > 0) {
      annualized = Math.pow(1 + totalTwr, 1 / years) - 1;
    }
  }
  
  // Fallback to trading days calculation if date-based calculation fails
  if (isNaN(annualized) && n > 0 && Number.isFinite(totalTwr)) {
    annualized = Math.pow(1 + totalTwr, tradingDays / n) - 1;
  }

  const rets = series.map((x) => x.r);
  const vol =
    rets.length >= (ddof === 1 ? 2 : 1)
      ? std(rets, ddof) * Math.sqrt(tradingDays)
      : NaN;

  const avgDD = drawdowns.length ? mean(drawdowns) : NaN;

  // Recovery date
  let recoveryDate = effectiveEndDate ?? null;
  let daysToRecover = null;

  if (n > 0 && worstDD < 0) {
    const troughIdx = equityPath.findIndex((x) => x.date === worstTroughDate);
    if (troughIdx >= 0) {
      const after = equityPath.slice(troughIdx + 1);
      const recovered = after.find((x) => x.equity >= worstPeakValue);
      if (recovered) {
        recoveryDate = recovered.date;
        daysToRecover = dayDiffCalendar(worstTroughDate, recoveryDate);
      } else {
        recoveryDate = equityPath[equityPath.length - 1].date;
        daysToRecover = null;
      }
    }
  }

  const daysFromStartToEnd =
    worstPeakDate && recoveryDate
      ? dayDiffCalendar(worstPeakDate, recoveryDate)
      : null;

  return {
    backtest: {
      startDate,
      endDate,
      n,
      effectiveStartDate,
      effectiveEndDate,
    },

    totalTimeWeightedReturnPct: totalTwr * 100,
    annualizedReturnPct: annualized * 100,

    sharpe: sharpeRatio(dailyReturns, startDate, endDate, {
      tradingDays,
      riskFreeRateAnnual,
      ddof,
      sortinoThreshold,
      ignoreNonTradingDays,
    }),

    sortino: sortinoRatio(dailyReturns, startDate, endDate, {
      tradingDays,
      riskFreeRateAnnual,
      ddof,
      sortinoThreshold,
      ignoreNonTradingDays,
    }),

    volatilityPct: vol * 100,

    averageDrawdownPct: avgDD * 100,

    worstDrawdown: {
      startDate: n ? worstPeakDate : null,
      endDate: n ? recoveryDate : null,
      troughDate: n ? worstTroughDate : null,
      daysToRecover: n ? daysToRecover : null,
      daysFromStartToEnd: n ? daysFromStartToEnd : null,
      startValue: n ? worstPeakValue : null,
      troughValue: n ? worstTroughValue : null,
      drawdownPct: worstDD * 100, // negative percent
    },
  };
}

export {
  sharpeRatio,
  sortinoRatio,
  maxDrawdownPct,
  backtestSummaryFromNav,
  backtestSummary,
  periodReturnFromNav,
};
